import { SearchIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Divider,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { AssignPrescriberListItem } from './AssignPrescriberListItem'
import type { PrescriberProfile, Location } from '../../../types/location'
import type { OrganizationUser } from '../../users/UsersSlice'

interface AssignPrescribersModalProps {
  isOpen: boolean
  location: Location | null
  users: OrganizationUser[]
  isAssigning?: boolean
  onClose: () => void
  onAssign: (selectedPrescriberIds: string[]) => void
}

const formatUserName = (user: OrganizationUser): string =>
  [user.firstName, user.lastName].filter(Boolean).join(' ').trim()

export const AssignPrescribersModal = ({
  isOpen,
  location,
  users,
  isAssigning = false,
  onClose,
  onAssign,
}: AssignPrescribersModalProps) => {
  const [searchValue, setSearchValue] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  useEffect(() => {
    if (isOpen) {
      setSearchValue('')
      setSelectedIds([])
    }
  }, [isOpen])

  const assignablePrescribers = useMemo(() => {
    if (!location) return []

    const assignedPrescriberIds = new Set(
      location.prescribers?.map((prescriber: PrescriberProfile) => prescriber.prescriberId) ?? []
    )

    return users
      .filter(
        (
          user
        ): user is OrganizationUser & { prescriber: NonNullable<OrganizationUser['prescriber']> } =>
          Boolean(user.prescriber?.active) && !assignedPrescriberIds.has(user.prescriber?.id ?? '')
      )
      .map((user) => ({
        id: user.prescriber.id,
        title: user.title,
        name: formatUserName(user),
        subtitle: user.prescriber.entityId ? `Provider number: ${user.prescriber.entityId}` : '',
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [users, location])

  const filteredPrescribers = useMemo(() => {
    const normalizedQuery = searchValue.trim().toLowerCase()
    if (!normalizedQuery) return assignablePrescribers

    return assignablePrescribers.filter((prescriber) =>
      `${prescriber.name} ${prescriber.subtitle}`.toLowerCase().includes(normalizedQuery)
    )
  }, [assignablePrescribers, searchValue])

  const emptyStateMessage =
    assignablePrescribers.length === 0
      ? 'All prescribers are already assigned to this location.'
      : 'No prescribers found'

  const handleTogglePrescriber = (prescriberId: string, checked: boolean) => {
    setSelectedIds((previous) => {
      if (checked) {
        return previous.includes(prescriberId) ? previous : [...previous, prescriberId]
      }

      return previous.filter((currentId) => currentId !== prescriberId)
    })
  }

  const handleAssign = () => {
    onAssign(selectedIds)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered closeOnOverlayClick={false}>
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent borderRadius="xl" overflow="hidden">
        <ModalHeader
          fontSize="md"
          fontWeight="bold"
          lineHeight="1.2"
          color="gray.900"
          px={5}
          pt={4}
          pb={3}
        >
          Assign Prescribers
        </ModalHeader>
        <ModalCloseButton
          top={3}
          right={3}
          size="sm"
          border="1px"
          borderColor="gray.200"
          borderRadius="md"
          color="gray.400"
          w="24px"
          h="24px"
          _hover={{ bg: 'gray.50' }}
          aria-label="Close assign prescribers modal"
        />

        <Divider borderColor="gray.100" />

        <ModalBody py={4} px={5}>
          <VStack spacing={3} align="stretch">
            <Box border="1px" borderColor="gray.200" bg="gray.50" borderRadius="md" px={3} py={2}>
              <HStack spacing={2}>
                <Text fontSize="sm" fontWeight="semibold" lineHeight="1.25" color="gray.600">
                  Assigning to:
                </Text>
                <Text fontSize="sm" fontWeight="bold" lineHeight="1.25" color="gray.900">
                  {location?.name ?? ''}
                </Text>
              </HStack>
            </Box>

            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none" color="gray.400">
                <SearchIcon boxSize={3.5} />
              </InputLeftElement>
              <Input
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search prescribers..."
                borderColor="gray.200"
                bg="gray.50"
                borderRadius="md"
                _hover={{ borderColor: 'gray.300' }}
                _focusVisible={{
                  borderColor: 'blue.400',
                  boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                }}
                aria-label="Search prescribers"
              />
            </InputGroup>

            <HStack justify="space-between" align="center" px={1}>
              <Text
                fontSize="xs"
                fontWeight="bold"
                textTransform="uppercase"
                letterSpacing="wider"
                color="gray.400"
              >
                Select Prescribers
              </Text>
              <Text fontSize="xs" fontWeight="bold" color="blue.500">
                {`${selectedIds.length} selected`}
              </Text>
            </HStack>

            <Box
              border="1px"
              borderColor="gray.200"
              borderRadius="md"
              maxH="280px"
              overflowY="auto"
            >
              {filteredPrescribers.length === 0 ? (
                <Box px={6} py={10}>
                  <Text fontSize="xs" color="gray.500" textAlign="center">
                    {emptyStateMessage}
                  </Text>
                </Box>
              ) : (
                filteredPrescribers.map((prescriber) => (
                  <AssignPrescriberListItem
                    key={prescriber.id}
                    id={prescriber.id}
                    title={prescriber.title}
                    name={prescriber.name}
                    subtitle={prescriber.subtitle}
                    isChecked={selectedIds.includes(prescriber.id)}
                    onToggle={handleTogglePrescriber}
                  />
                ))
              )}
            </Box>
          </VStack>
        </ModalBody>

        <Divider borderColor="gray.100" />

        <ModalFooter py={3} px={5}>
          <HStack justify="flex-end" w="full" spacing={2}>
            <Button
              variant="outline"
              colorScheme="blue"
              borderColor="blue.300"
              color="blue.600"
              onClick={onClose}
              minW="80px"
              size="sm"
              borderRadius="md"
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleAssign}
              isDisabled={selectedIds.length === 0}
              isLoading={isAssigning}
              minW="80px"
              size="sm"
              borderRadius="md"
            >
              Assign
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
